import React, { Fragment, useEffect, useMemo } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  AssetParams,
  CollectorSet,
  Flex,
  FormikAPIFormValues,
  KeyLabel,
  Segment,
  useAssetConfig,
  useAssetParams,
} from '@mss/mss-component-library';
import { Checkbox as AntdCheckbox, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Info } from 'react-feather';
import classNames from 'classnames';

type SelectCollectorSegmentProps = {
  collectors: CollectorSet;
  set: string;
  formIndex?: number;
  overrideAssetParams?: Partial<AssetParams>;
};

export const CheckboxWrapper = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={classNames(
        '[&_.ant-checkbox-group-item]:block [&_.ant-checkbox-group-item]:mb-1.5 [&_.ant-checkbox-group-item]:ml-2',
        '[&_.ant-checkbox-wrapper]:block [&_.ant-checkbox-wrapper]:mb-1.5 [&_.ant-checkbox-wrapper]:ml-2',
        className
      )}
      {...props}
    />
  );
};

type CollectorSetValue = string | { extra_options: any; type: string };

export default function CollectorSegment({
  collectors,
  set,
  formIndex = 0,
  overrideAssetParams,
}: SelectCollectorSegmentProps) {
  const { setFieldValue, values } =
    useFormikContext<FormikAPIFormValues<any>>();
  const fieldName = `assets.${formIndex}.values.${set}`;

  const [t] = useTranslation(['asset', 'common']);

  const { assetSubType } = useAssetParams(overrideAssetParams);
  const { matchingConfig } = useAssetConfig(assetSubType);

  const antdOptions = collectors.collectors.map(
    (x: KeyLabel & { tooltip?: string }) => ({
      label: x.label,
      value: x.key,
      tooltip: x.tooltip,
    })
  );

  const everyOptionValue = useMemo(
    () =>
      antdOptions.reduce((acc, option) => {
        const extraOptions =
          matchingConfig.form.collectors.extraOptions?.[option.value];
        return [
          ...acc,
          ...(extraOptions ? (collectors.extra_options ?? []) : []),
          option.value,
        ];
      }, [] as string[]),
    [antdOptions, matchingConfig.form.collectors.extraOptions]
  );

  useEffect(() => {
    if (!(values as any).assets[formIndex].values) {
      setFieldValue(fieldName, []);
    }
  }, [formIndex, set, setFieldValue, values, fieldName]);

  const collectorSetValues: CollectorSetValue[] =
    (values as any).assets[formIndex].values?.[set] ?? [];

  const handleCheckAll = (e: CheckboxChangeEvent) => {
    setFieldValue(
      fieldName,
      e.target.checked
        ? antdOptions
            .map(x => {
              const extraOptions =
                matchingConfig.form.collectors.extraOptions?.[x.value];

              return extraOptions
                ? {
                    type: x.value,
                    extra_options: {
                      filter_categories: collectors.extra_options ?? [],
                    },
                  }
                : x.value;
            })
            .flat()
        : []
    );
  };

  const defaultOnChange = (option: { value: string }) => () =>
    setFieldValue(
      fieldName,
      collectorSetValues.includes(option.value)
        ? collectorSetValues.filter(
            (x: CollectorSetValue) => x !== option.value
          )
        : [...collectorSetValues, option.value]
    );

  const onChangeParentExtraOptions = (option: { value: string }) => () => {
    setFieldValue(
      fieldName,
      collectors.extra_options?.every(
        extraOption =>
          !!collectorSetValues.find((value: CollectorSetValue) => {
            if (typeof value === 'string') {
              return false;
            }
            return value.extra_options.filter_categories?.includes(extraOption);
          })
      )
        ? collectorSetValues.filter((value: CollectorSetValue) => {
            if (typeof value === 'string') {
              return value !== option.value;
            }
            return value.type !== option.value;
          })
        : collectorSetValues.reduce(
            (acc, val) => {
              if (typeof val === 'string') {
                return [...acc, val];
              }
              return acc;
            },
            [
              {
                type: option.value,
                extra_options: { filter_categories: collectors.extra_options },
              },
            ] as CollectorSetValue[]
          )
    );
  };

  const onChangeExtraOptions =
    (parentOption: string, extraOption: string) => () => {
      const currentExtraOptionsValues = collectorSetValues.find(
        (value: CollectorSetValue) => {
          if (typeof value === 'string') {
            return false;
          }
          return value.type === parentOption;
        }
      );

      if (currentExtraOptionsValues) {
        setFieldValue(
          fieldName,
          collectorSetValues.map(x => {
            if (typeof x === 'string') {
              return x;
            }
            if (x.type === parentOption) {
              return {
                type: parentOption,
                extra_options: {
                  filter_categories: x.extra_options.filter_categories.includes(
                    extraOption
                  )
                    ? x.extra_options.filter_categories.filter(
                        (value: string) => value !== extraOption
                      )
                    : [...x.extra_options.filter_categories, extraOption],
                },
              };
            }
            return x;
          })
        );
      } else {
        setFieldValue(fieldName, [
          ...collectorSetValues,
          {
            type: parentOption,
            extra_options: { filter_categories: [extraOption] },
          },
        ]);
      }
    };

  return (
    <Segment
      padding
      border
      key={collectors.label}
      heading={
        <Flex h="between" v="center">
          {collectors.label}
          <AntdCheckbox
            onChange={handleCheckAll}
            checked={
              everyOptionValue.length ===
              collectorSetValues?.reduce((acc, val) => {
                return [
                  ...acc,
                  ...(typeof val === 'string'
                    ? [val]
                    : [val.type, ...val.extra_options.filter_categories]),
                ];
              }, [] as string[]).length
            }
            data-ta={`collectors${collectors.label}CheckAll`}
          >
            {t('Check all')}
          </AntdCheckbox>
        </Flex>
      }
    >
      {
        <CheckboxWrapper>
          {antdOptions.map(option => {
            const extraOptionsMapping =
              matchingConfig.form.collectors.extraOptions?.[option.value];
            const hasExtraOptions = !!extraOptionsMapping;
            const extraOptionsValues = collectorSetValues.find(
              x => typeof x !== 'string' && x.type === option.value
              // @ts-ignore
            )?.extra_options.filter_categories;

            const defaultChecked =
              collectorSetValues?.includes(option.value) ?? false;
            const extraOptionsSomeChecked =
              extraOptionsValues &&
              collectors.extra_options?.some(extraOption =>
                extraOptionsValues?.includes(extraOption)
              );
            const extraOptionsAllChecked =
              extraOptionsValues &&
              collectors.extra_options?.every(extraOption =>
                extraOptionsValues?.includes(extraOption)
              );

            const extraLabel =
              matchingConfig.form.collectors?.labels?.[option.value];
            const extraLabelComponent = extraLabel ? (
              <span className="border rounded text-[11px] leading-3 bg-primary text-white px-1.5 py-[2px] ml-3">
                {extraLabel}
              </span>
            ) : null;
            return (
              <Fragment key={option.value}>
                <AntdCheckbox
                  key={option.value}
                  name={option.value}
                  onChange={
                    hasExtraOptions
                      ? onChangeParentExtraOptions(option)
                      : defaultOnChange(option)
                  }
                  indeterminate={
                    hasExtraOptions &&
                    extraOptionsSomeChecked &&
                    !extraOptionsAllChecked
                  }
                  checked={
                    hasExtraOptions ? extraOptionsAllChecked : defaultChecked
                  }
                >
                  {option.tooltip ? (
                    <Tooltip placement="right" title={option.tooltip}>
                      <span>{option.label}</span>
                      <Info size={14} className="text-gray-400 ml-1" />
                      {extraLabelComponent}
                    </Tooltip>
                  ) : (
                    <>
                      <span>{option.label}</span>
                      {extraLabelComponent}
                    </>
                  )}
                </AntdCheckbox>
                {hasExtraOptions && (
                  <Flex h="between" v="center" className="ml-6 mt-2">
                    <CheckboxWrapper>
                      {collectors.extra_options?.map(extra_option => {
                        return (
                          <AntdCheckbox
                            key={extra_option}
                            name={extra_option}
                            onChange={onChangeExtraOptions(
                              option.value,
                              extra_option
                            )}
                            checked={collectorSetValues
                              .find(
                                x =>
                                  typeof x !== 'string' &&
                                  x.type === option.value
                              )
                              // @ts-ignore
                              ?.extra_options.filter_categories.includes(
                                extra_option
                              )}
                          >
                            <span>{extraOptionsMapping?.[extra_option]}</span>
                          </AntdCheckbox>
                        );
                      })}
                    </CheckboxWrapper>
                  </Flex>
                )}
              </Fragment>
            );
          })}
        </CheckboxWrapper>
      }
    </Segment>
  );
}
